export type TotalBetOverrider = (totalBet: number) => number;

/**
 * **BetOverrider** is used to override the total bet calculation. This is useful when a game has a special multiplier on the whole bet.
 *
 * It uses the totalBetOverrider function to calculate the total bet. This can be set by the setTotalBetOverrider method.
 *
 * Each time totalBetOverrider function has changed, the handleTotalBetOverriderChange function is called, which allows triggering side effects,
 * like, for example, recalculating the total bet, if function has either changed or was removed.
 *
 * To utilise this functionality in the game, game should just set the totalBetOverrider function to the desired one, when necessary.
 * **If game does not need to override the total bet calculation - no extra logic on the game side is needed.**
 *
 * @property totalBetOverrider - the function that overrides the total bet calculation
 * @property defaultTotalBetOverrider - the default total bet overrider function, which just returns the total bet.
 * @property handleTotalBetOverriderChange - the function that is called when the total bet overrider is changed
 *
 * @method setTotalBetOverrider - sets the total bet overrider function
 * @method removeTotalBetOverrider - removes the total bet overrider function, and uses the default one
 * @method onTotalBetOverriderChange - sets the function that is called when the total bet overrider is changed. **Not meant to be used by a game.**
 * @internal
 * */
export class BetOverrider {
  public static totalBetOverrider: TotalBetOverrider = (totalBet) => totalBet;
  static defaultTotalBetOverrider: TotalBetOverrider = (totalBet) => totalBet;
  public static handleTotalBetOverriderChange: (() => void) | null = null;

  public static setTotalBetOverrider(totalBetOverrider: TotalBetOverrider): void {
    this.totalBetOverrider = totalBetOverrider;
    this.handleTotalBetOverriderChange?.();
  }

  public static removeTotalBetOverrider(): void {
    this.totalBetOverrider = this.defaultTotalBetOverrider;
    this.handleTotalBetOverriderChange?.();
  }

  public static onTotalBetOverriderChange(callback: (() => void) | null): void {
    this.handleTotalBetOverriderChange = callback;
  }
}
